import { useTranslations } from 'next-intl'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import * as RPNInput from 'react-phone-number-input'
import { I18nMessages, MetaData, RouteParams } from '@/types/meta'
import { CategoryType } from '@/types/category'
import { type TranslatableType } from '@/types/general'
import { ProductType } from '@/types/product'
import config from '@/config'

// Date utilities from date-utils module
export { formatDateTime, formatLongDate, formatLongDateTime, formatDate } from './date-utils'

/**
 * Strips HTML tags from a string and returns plain text.
 *
 * @param inputs ClassValue[]
 * @returns {string} Merged class names.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs))

/**
 * Get the base URL for the application
 * Uses request headers to determine the current host
 */
export const getBaseUrl = (): string => {
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return process.env.NEXT_PUBLIC_BASE_URL
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`
  }

  return 'https://www.bumeks.com.tr'
}

/**
 * Returns an array of available locales in the format "code-countryCode".
 *
 * @param metaData
 * @returns {string[]} An array of available locales.
 */
export const getAvailableLocales = (metaData: MetaData): string[] => {
  return metaData.locales.map(l => `${l.code}-${l.countryCode}`)
}

/**
 * Returns the requested locale if it is available, otherwise returns the default locale.
 *
 * @param metaData
 * @param locale
 * @returns {string} The requested locale or the default locale.
 */
export const getRequestedLocale = (metaData: MetaData, locale: string): string => {
  return getAvailableLocales(metaData).includes(locale) ? locale : metaData.generalSettings.appDefaultLocale
}

/**
 * Formats messages for use with next-intl.
 *
 * @param messages Record<string, string>
 * @param locale string
 * @param fallbackMessages
 * @param defaultLocale
 * @returns {Record<string, string>} Formatted messages for the specified locale.
 */
export const formatMessagesForNextIntl = (
  messages: I18nMessages,
  locale: string,
  fallbackMessages: Record<string, string> = {},
  defaultLocale?: string
): Record<string, string> => {
  const formatted: Record<string, string> = fallbackMessages
  for (const [key, translations] of Object.entries(messages)) {
    // Try current locale, then defaultLocale, then fall back to en-US
    formatted[key] =
      translations[locale] ||
      (defaultLocale && translations[defaultLocale]) ||
      translations['en-US'] ||
      Object.values(translations)[0] // Final fallback to the first available translation
  }

  return formatted
}

/**
 * A utility function that retrieves a translated value for a given key if it exists in the translation object,
 * otherwise returns the key itself as a fallback.
 *
 * @param {ReturnType<typeof useTranslations>} t - The translation object, typically returned from the `useTranslations` hook,
 * containing methods for handling translations.
 * @param {string} key - The identifier string used to look up the translated value from the translation object.
 * @param {Record<string, any>} values - Optional values object for translation interpolation (e.g., {count: 5}).
 * @returns {string} The translated value corresponding to the provided key if it exists, or the key itself if no translation is found.
 */
export const tOptional = (t: ReturnType<typeof useTranslations>, key: string, values?: Record<string, any>): string =>
  t.has(key) ? t(key, values) : key

/**
 * Extract error property from API error response
 * @param error any - The error object from API response
 * @param defaultMessage string - Default property to return if no error property is found
 * @returns string - The extracted error property
 */
export const getErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  if (!error) {
    return defaultMessage
  }

  if (error.response) {
    const { data } = error.response

    if (data?.message) {
      return data.message
    }

    if (data?.error?.message) {
      return data.error.message
    }

    if (data?.error && typeof data.error === 'string') {
      return data.error
    }
  }

  if (error.message) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return defaultMessage
}

/**
 * Set validation errors on a form using react-hook-form.
 *
 * @param error any - The error object from the backend
 * @param setError any - The form.setError function from react-hook-form
 */
export const setValidationErrors = (error: any, setError: any) => {
  const errors = error?.response?.data?.items || {}
  Object.entries(errors).forEach(([key, message]) => {
    // Convert backend field names to react-hook-form field paths
    // e.g., "oemNumbers[0]" -> "oemNumbers.0"
    // e.g., "translations[en-US][title]" -> "translations.en-US.title"
    const fieldPath = key.replace(/\[(\d+)]/g, '.$1').replace(/\[([^\]]+)]/g, '.$1')
    setError(fieldPath, {
      type: 'manual',
      message: message as string
    })
  })
}

/**
 * Checks if a JWT token is valid by making a request to the /account/me endpoint.
 *
 * @param token string
 * @returns {Promise<boolean>} Returns true if the token is valid, false otherwise.
 */
export const isVerifiedJwt = async (token: string): Promise<boolean> => {
  try {
    await fetch(`${config.API_BASE_URL}/account/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })

    return true
  } catch (error: any) {
    console.error(error)
    return false
  }
}

/**
 * Limit text
 * @param text string
 * @param limit number
 */
export const textLimit = (text: string, limit: number = 100) => {
  if (!text) {
    return ''
  }

  return text.length > limit ? text.slice(0, limit) + '...' : text
}

/**
 * Generate JSON-LD schema dynamically
 * @param type - Schema.org type (e.g., 'Article', 'Event', 'ImageGallery', 'Product', etc.)
 * @param title - Main title/name (automatically mapped to 'name' or 'headline' based on type)
 * @param description - Description/content (HTML will be stripped automatically)
 * @param image - Image URL (optional)
 * @param additionalProperties - Any additional schema properties specific to the type
 * @returns Schema.org JSON-LD object
 */
export const generateSchema = (
  type: string,
  title: string,
  description?: string,
  image?: string,
  additionalProperties?: Record<string, any>
) => {
  // Base schema structure
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': type
  }

  // Determine title field name based on type
  // Article uses 'headline', most others use 'name'
  const titleField = type === 'Article' ? 'headline' : 'name'
  schema[titleField] = title

  // Add description if provided (strip HTML)
  if (description) {
    schema.description = stripHtmlTags(description)
  }

  // Add image if provided
  if (image) {
    schema.image = image
  }

  // Merge additional properties (filter out undefined/null values)
  if (additionalProperties) {
    Object.entries(additionalProperties).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        schema[key] = value
      }
    })
  }

  return schema
}

/**
 * Generate OpenGraph metadata
 * @param title - OG title
 * @param description - OG description (HTML will be stripped)
 * @param image - OG image URL (defaults to brand-logo.png if not provided)
 * @param url - OG URL
 * @param type - OG type (default: 'website')
 * @param additionalProperties - Additional OG properties
 */
export const generateOpenGraph = (
  title: string,
  description?: string,
  image?: string,
  url?: string,
  type: string = 'website',
  additionalProperties?: Record<string, any>
) => {
  const baseUrl = getBaseUrl()
  const ogImage = image || `${baseUrl}/brand-logo.png`

  return {
    title,
    description: description ? stripHtmlTags(description) : undefined,
    images: [
      {
        url: ogImage,
        width: 1200,
        height: 630,
        alt: title
      }
    ],
    url,
    type,
    ...additionalProperties
  }
}

/**
 * Generate Twitter Card metadata
 * @param title - Twitter card title
 * @param description - Twitter card description (HTML will be stripped)
 * @param image - Twitter card image URL (defaults to brand-logo.png if not provided)
 * @param card - Twitter card type (default: 'summary_large_image')
 */
export const generateTwitterCard = (
  title: string,
  description?: string,
  image?: string,
  card: 'summary' | 'summary_large_image' | 'app' | 'player' = 'summary_large_image'
) => {
  const baseUrl = getBaseUrl()
  const twitterImage = image || `${baseUrl}/brand-logo.png`

  return {
    card,
    title,
    description: description ? stripHtmlTags(description) : undefined,
    images: [
      {
        url: twitterImage,
        alt: title
      }
    ]
  }
}

/**
 * Generate complete page metadata with OpenGraph and Twitter Card
 * @param title - Page title
 * @param description - Page description
 * @param canonical - Canonical URL
 * @param image - OG/Twitter image URL (defaults to brand-logo.png if not provided)
 * @param type - OG type (default: 'website')
 * @param additionalMetadata - Additional metadata properties
 */
export const generatePageMetadata = (
  title: string,
  description?: string,
  canonical?: string,
  image?: string,
  type: string = 'website',
  additionalMetadata?: Record<string, any>
) => {
  const baseUrl = getBaseUrl()
  const metaImage = image || `${baseUrl}/brand-logo.png`

  return {
    title,
    description: description ? stripHtmlTags(description) : undefined,
    alternates: canonical
      ? {
          canonical
        }
      : undefined,
    openGraph: generateOpenGraph(title, description, metaImage, canonical, type),
    twitter: generateTwitterCard(title, description, metaImage),
    ...additionalMetadata
  }
}

/**
 * Creates a URLSearchParams object from a given parameters object.
 *
 * @param params Record<string, any> - An object containing key-value pairs to be converted into URL search parameters.
 * @returns {URLSearchParams} A URLSearchParams object containing the provided parameters.
 */
export const createSearchParams = (params?: Record<string, any>): URLSearchParams => {
  const searchParams = new URLSearchParams()

  if (params === undefined || params === null) {
    return searchParams
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          searchParams.append(key, value.join(','))
        }
      } else {
        searchParams.append(key, value.toString())
      }
    }
  })

  return searchParams
}

/**
 * Get country code from locale
 * Extract country code from locale (e.g., 'en-US' -> 'US', 'tr-TR' -> 'TR')
 *
 * @param locale string - Locale in the format 'en-US', 'tr-TR', etc.
 * @returns string - Country code in uppercase (e.g., 'US', 'TR')
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  const parts = locale.split('-')
  return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
}

/**
 * Detect user's country code from browser locale settings
 *
 * @returns string | undefined - Detected country code in uppercase (e.g., 'US', 'TR') or undefined if detection fails
 */
export const detectCountryFromLocale = (): RPNInput.Country | undefined => {
  try {
    if (typeof navigator === 'undefined') {
      return undefined
    }
    const region = new Intl.Locale(navigator.language).region
    return region as RPNInput.Country
  } catch {
    return undefined
  }
}

/**
 * Check if a translatable object has a translation for the given locale
 * @param data TranslatableType - The translatable object containing translations
 * @param localeCode string - The locale code to check (e.g., 'en-US', 'tr-TR')
 * @returns boolean - True if the translation exists, false otherwise
 */
export const hasTranslation = (data: TranslatableType, localeCode: string): boolean => {
  return data.translations?.[localeCode] !== undefined
}

/**
 * Converts a lowercase URL locale (e.g., "en-us") to backend format (e.g., "en-US").
 *
 * @param locale string - The locale string in lowercase format
 * @returns {string} The locale string in backend format
 */
export const toBackendLocale = (locale: string): string => {
  const parts = locale.split('-')
  if (parts.length === 2) {
    return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`
  }
  return locale
}

/**
 * Convert URL locale format (e.g., tr-tr) to backend locale format (e.g., tr-TR)
 *
 * @param urlLocale - The locale from the URL (e.g., 'tr-tr', 'en-us')
 * @returns The locale in backend format (e.g., 'tr-TR', 'en-US')
 */
export const toBackendLocaleFormat = (urlLocale: string): string => {
  const [lang, region] = urlLocale.split('-')
  if (!lang || !region) {
    return urlLocale
  }

  return `${lang.toLowerCase()}-${region.toUpperCase()}`
}

/**
 * Reverse lookup: Find the route key from a translated segment
 *
 * @param segment - The URL segment (e.g., 'kurumsal', 'corporate')
 * @param locale - The locale (e.g., 'tr-TR', 'en-US')
 * @param routeParams - The route params from API
 * @returns The route key (e.g., 'corporate') or null if not found
 */
export const getRouteKeyFromSegment = (segment: string, locale: string, routeParams: RouteParams): string | null => {
  // Search through all route params to find which key has this segment as a value for this locale
  for (const [routeKey, translations] of Object.entries(routeParams)) {
    if (translations[locale] === segment) {
      return routeKey
    }
  }

  return null
}

/**
 * Get a localized route from route keys
 *
 * @param path - The route path with keys (e.g., '/products/electronics')
 * @param locale - The locale (e.g., 'tr-TR', 'en-US')
 * @param routeParams - The route params from API
 * @returns The localized route separated by '/' (e.g., 'urunler/elektronik')
 */
export const getLocalizedRouteFromKeys = (path: string, locale: string, routeParams: RouteParams): string => {
  const hasLeadingSlash = path.startsWith('/')

  const localizedPath = path
    .split('/')
    .filter(Boolean)
    .map(segment => routeParams[segment]?.[locale] ?? segment)
    .join('/')

  return hasLeadingSlash ? `/${localizedPath}` : localizedPath
}

/**
 * Extracts and parses the Accept-Language header to find the best matching locale.
 * Returns the best matching supported locale or null if none is found.
 *
 * @param acceptLanguage - The Accept-Language header string from the request.
 * @param availableLocales - Array of supported locale strings (e.g., ['en-us', 'tr-tr']).
 * @returns The best matching locale string or null if no match is found.
 */
export const getBrowserLocale = (acceptLanguage: string | null, availableLocales: string[]): string | null => {
  if (!acceptLanguage) {
    return null
  }

  // Parse Accept-Language header (e.g., "en-US,en;q=0.9,tr-TR;q=0.8,tr;q=0.7")
  const languages = acceptLanguage
    .split(',')
    .map(lang => {
      const [locale, qValue] = lang.trim().split(';q=')
      return {
        locale: locale.toLowerCase(),
        quality: qValue ? parseFloat(qValue) : 1.0
      }
    })
    .sort((a, b) => b.quality - a.quality) // Sort by quality (preference)

  // Try to find the exact match first (e.g., "en-us")
  for (const { locale } of languages) {
    if (availableLocales.includes(locale)) {
      return locale
    }
  }

  // Try to find a language match (e.g., "en" matches "en-us")
  for (const { locale } of languages) {
    const langCode = locale.split('-')[0]
    const match = availableLocales.find(available => available.startsWith(langCode + '-'))
    if (match) {
      return match
    }
  }

  return null
}

/**
 * Get localized value from a translatable object
 * @param data ITranslatable - The translatable object containing translations
 * @param field string - The field to retrieve from translations (e.g., 'name', 'description')
 * @param locale string - The locale to use for translation (e.g., 'en-US', 'tr-TR')
 * @param defaultLocale string - The default locale to fall back to if the requested locale is not available (default: 'en-US')
 */
export const getLocalizedValue = (
  data: TranslatableType,
  field: string,
  locale: string,
  defaultLocale: string = 'en-US'
) => {
  if (!data || !data.translations) {
    return ''
  }

  const backendLocale = toBackendLocale(locale)

  // Try to get the current locale's translation
  if (data.translations[backendLocale]) {
    return data.translations[backendLocale][field as keyof (typeof data.translations)[typeof backendLocale]] || ''
  }

  // Try to get the defaultLocale's translation
  if (defaultLocale && data.translations[defaultLocale]) {
    return data.translations[defaultLocale][field as keyof (typeof data.translations)[typeof defaultLocale]] || ''
  }

  // Fallback to the first available translation
  const firstLocale = Object.keys(data.translations)[0]
  return firstLocale
    ? data.translations[firstLocale][field as keyof (typeof data.translations)[typeof firstLocale]] || ''
    : ''
}

export const getCategoryPath = (category: CategoryType, locale: string, defaultLocale?: string) => {
  if (!category.parents || category.parents.length === 0) {
    return '-'
  }

  // Get all parent names in the correct order
  const parentNames = category.parents.map(parent => {
    if (parent.translations[locale]) {
      return parent.translations[locale].name
    }

    // Try to get the defaultLocale's translation
    if (defaultLocale && parent.translations[defaultLocale]) {
      return parent.translations[defaultLocale].name
    }

    // Fallback to the first available translation
    const firstLocale = Object.keys(parent.translations)[0]
    return parent.translations[firstLocale]?.name || 'Unnamed Category'
  })

  return parentNames.join(' > ')
}

/**
 * Get picture URL
 *
 * @param path string
 * @returns string
 */
export const getPictureUrl = (path: string): string => `/api/shared/media/picture/show/${path}`

/**
 * Get video streaming URL. Uses the dedicated video endpoint which supports HTTP range
 * requests, so playback can start and seek without downloading the whole file first.
 *
 * @param path string
 * @returns string
 */
export const getVideoUrl = (path: string): string => `/api/shared/media/video/show/${path}`

/**
 * Get resized picture URL
 *
 * @param path string
 * @param width number
 * @param height number
 * @returns string
 */
export const getResizedPictureUrl = (path: string, width: number, height: number): string =>
  `/api/shared/media/picture/${width}/${height}/${path}`

/**
 * Converts file size in bytes to the most appropriate unit (KB, MB, GB, TB) with formatted output
 *
 * @param bytes number - The file size in bytes
 * @param decimals number - Number of decimal places to include in the formatted output (default: 2)
 * @returns string - The file size with the appropriate unit suffix
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) {
    return '0 Bytes'
  }

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  // Prevent array index out of bounds
  const sizeIndex = Math.min(i, sizes.length - 1)
  const value = bytes / Math.pow(k, sizeIndex)

  return value.toFixed(decimals) + ' ' + sizes[sizeIndex]
}

/**
 * Formats a number as USD currency
 *
 * @param value - The numeric value to format
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @param currency
 * @returns string - The formatted currency string
 */
export const formatMoney = (value: number | undefined | null, locale: string = 'en-US', currency = 'USD'): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

/**
 * Formats a number according to the specified locale
 *
 * @param value - The numeric value to format
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @returns string - The formatted number string
 */
export const formatNumber = (value: number | undefined | null, locale: string = 'en-US'): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Get file extension from file name
 *
 * @param filename
 * @return string - File extension in lowercase (e.g., 'jpg', 'png')
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

/**
 * Strips HTML tags from a string and returns plain text.
 * Also decodes common HTML entities.
 *
 * @param html - The HTML string to strip tags from
 * @returns {string} Plain text without HTML tags
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) {
    return ''
  }

  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, '')

  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&hellip;/g, '...')

  // Remove extra whitespace
  text = text.replace(/\s+/g, ' ').trim()

  return text
}

/**
 * Calculate event status
 *
 * @param beginDate
 * @param endDate
 */
export const getEventStatus = (beginDate: string, endDate: string) => {
  const now = new Date()
  const start = new Date(beginDate)
  const end = new Date(endDate)
  const totalDuration = end.getTime() - start.getTime()
  const elapsed = now.getTime() - start.getTime()
  const percentComplete = (elapsed / totalDuration) * 100

  if (now < start) {
    return {
      key: 'upcoming',
      className: 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
    }
  } else if (now > end) {
    return {
      key: 'completed',
      className: 'bg-gray-500 text-white hover:bg-gray-600 border-gray-500'
    }
  } else if (percentComplete >= 80) {
    return {
      key: 'ending_soon',
      className: 'bg-orange-500 text-white hover:bg-orange-600 border-orange-500'
    }
  } else {
    return {
      key: 'ongoing',
      className: 'bg-green-500 text-white hover:bg-green-600 border-green-500'
    }
  }
}

/**
 * Build category path from category and its parents
 *
 * @param category - Category with parents
 * @param backendLocale - Backend locale code
 * @returns Path string (e.g., "parent-slug/category-slug")
 */
export const buildCategoryPath = (category: CategoryType, backendLocale: string): string => {
  const pathSlugs: string[] = []

  if (category.parents && category.parents.length > 0) {
    // parents[0] is the immediate parent, parents[1] is grandparent, etc.
    // We need to reverse to get root -> parent order
    const reversedParents = [...category.parents].reverse()
    for (const parent of reversedParents) {
      const parentSlug = getLocalizedValue(parent, 'slug', backendLocale)
      if (parentSlug && parentSlug !== '-') {
        pathSlugs.push(parentSlug)
      }
    }
  }

  const slug = getLocalizedValue(category, 'slug', backendLocale)
  if (slug && slug !== '-') {
    pathSlugs.push(slug)
  }

  return pathSlugs.join('/')
}

/**
 * Append the first OEM's `brand/model/oem` path to a base product href so a list link points at the
 * product's first OEM-scoped page. Falls back to the plain product href when the product has no OEM
 * records. Use this everywhere a product is linked from a list (grid, related, repair kits).
 *
 * @param baseProductHref - The plain product detail href (e.g. `/urun/engine/mud-...-344003`)
 * @param product - The product (its `oemNumbers` are backend-ordered; the first valid one is used)
 * @returns The href, with the first OEM segment appended when available
 */
export const buildProductPath = (baseProductHref: string, product: ProductType): string => {
  const firstOem = product.oemNumbers?.find(oem => oem.slug && oem.model?.slug && oem.model?.brand?.slug)
  if (!firstOem) {
    return baseProductHref
  }
  return `${baseProductHref}/${firstOem.model.brand.slug}/${firstOem.model.slug}/${firstOem.slug}`
}

/**
 * Build a full path for a subcategory based on active category context
 *
 * @param subcategory - The subcategory to build a path for
 * @param activeCategory - Current active category
 * @param backendLocale - Backend locale code
 * @returns Full path string
 */
export const buildSubcategoryFullPath = (
  subcategory: CategoryType,
  activeCategory: CategoryType | undefined | null,
  backendLocale: string
): string => {
  // If subcategory has parents data, use it directly
  if (subcategory.parents?.length) {
    return buildCategoryPath(subcategory, backendLocale)
  }

  // No parents data, build manually using activeCategory context
  if (!activeCategory) {
    return buildCategoryPath(subcategory, backendLocale)
  }

  const pathSlugs: string[] = []

  // If activeCategory has children (meaning we're showing its children),
  // Use activeCategory's parents + activeCategory itself as the base path
  if (activeCategory.children?.length) {
    if (activeCategory.parents?.length) {
      const reversedParents = [...activeCategory.parents].reverse()
      for (const parent of reversedParents) {
        const parentSlug = getLocalizedValue(parent, 'slug', backendLocale)
        if (parentSlug && parentSlug !== '-') {
          pathSlugs.push(parentSlug)
        }
      }
    }
    // Add the activeCategory itself to the path
    const activeCategorySlug = getLocalizedValue(activeCategory, 'slug', backendLocale)
    if (activeCategorySlug && activeCategorySlug !== '-') {
      pathSlugs.push(activeCategorySlug)
    }
  } else {
    // activeCategory is a leaf, we're showing its siblings
    // Use activeCategory's parents (not including activeCategory itself)
    if (activeCategory.parents?.length) {
      const reversedParents = [...activeCategory.parents].reverse()
      for (const parent of reversedParents) {
        const parentSlug = getLocalizedValue(parent, 'slug', backendLocale)
        if (parentSlug && parentSlug !== '-') {
          pathSlugs.push(parentSlug)
        }
      }
    }
  }

  // Add subcategory slug
  const subcategorySlug = getLocalizedValue(subcategory, 'slug', backendLocale)
  if (subcategorySlug && subcategorySlug !== '-') {
    pathSlugs.push(subcategorySlug)
  }

  return pathSlugs.join('/')
}

/**
 * Build product breadcrumbs
 *
 * @param productsRoute
 * @param productRoute
 * @param product
 * @param backendLocale
 * @param t
 */
export const buildProductBreadcrumbs = (
  productsRoute: string,
  productRoute: string,
  backendLocale: string,
  t: ReturnType<typeof useTranslations>,
  product: ProductType
) => {
  // Build breadcrumb items
  const breadcrumbItems = [{ label: t('products'), href: productsRoute }]
  const slugPath: string[] = []
  const categories: CategoryType[] = [...(product.category.parents || []), product.category]

  // Add parent categories first (if they exist)
  for (const category of categories) {
    const parentName = getLocalizedValue(category, 'name', backendLocale)
    const parentSlug = getLocalizedValue(category, 'slug', backendLocale)
    if (parentName && parentSlug) {
      slugPath.push(parentSlug)
      breadcrumbItems.push({
        label: parentName,
        href: `${productsRoute}/${slugPath.join('/')}`
      })
    }
  }

  breadcrumbItems.push({
    label: getLocalizedValue(product, 'title', backendLocale),
    href: `${productRoute}/${getLocalizedValue(product, 'slug', backendLocale)}`
  })

  return breadcrumbItems
}
